// Reusable form fields shared across the four create forms. Mirrors
// the Drillcut `fields.tsx` set: DcInput, SegmentedToggle, TabChip,
// PrioritySelect.

import type { CSSProperties, InputHTMLAttributes, ReactNode } from "react";
import { DC, iconBtn } from "./primitives";
import { TODO_PRIORITY } from "../../../../../../../shared/team-planner/schemas";

interface DcInputProps extends InputHTMLAttributes<HTMLInputElement> {}

// Drillcut's DcInput — 38px, BSP-blue focus ring, inherits Barlow.
export function DcInput(props: DcInputProps) {
  return (
    <input
      {...props}
      style={{
        height: 38,
        padding: "0 12px",
        border: `1px solid ${DC.borderStrong}`,
        borderRadius: 6,
        background: DC.surface,
        font: "400 14px/1 var(--bsp-font-sans)",
        color: "var(--bsp-fg)",
        outline: 0,
        width: "100%",
        ...props.style,
      }}
    />
  );
}

interface SegmentedOption<T extends string> {
  v: T;
  label: string;
  hint?: string;
}

// Two-segment toggle (Draft / Committed). One option always active —
// no neutral state. Drillcut's component is identical apart from
// colours; reads BSP tokens here.
export function SegmentedToggle<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: SegmentedOption<T>[];
}) {
  return (
    <div
      role="radiogroup"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${options.length}, 1fr)`,
        gap: 0,
        border: `1px solid ${DC.borderStrong}`,
        borderRadius: 6,
        overflow: "hidden",
        background: DC.surface,
      }}
    >
      {options.map((opt, i) => {
        const active = opt.v === value;
        return (
          <button
            key={opt.v}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.v)}
            style={{
              height: 38,
              border: 0,
              borderLeft: i === 0 ? 0 : `1px solid ${DC.border}`,
              background: active ? DC.primaryTint : "transparent",
              color: active ? DC.primary : DC.grey,
              font: "700 12px/1 var(--bsp-font-sans)",
              letterSpacing: "0.04em",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <span>{opt.label}</span>
            {opt.hint ? (
              <span
                style={{
                  fontWeight: 400,
                  fontSize: 10,
                  color: active ? DC.primary : DC.grey,
                  letterSpacing: 0,
                }}
              >
                {opt.hint}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

// TabChip — the pill-style toggle used for Visibility (Team-visible /
// Private) and Assign-to (Individual / Team). Distinct from
// SegmentedToggle so the two visual languages don't collide.
export function TabChip({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        height: 32,
        padding: "0 14px",
        borderRadius: 999,
        border: `1px solid ${active ? DC.primary : DC.borderStrong}`,
        background: active ? DC.primaryTint : DC.surface,
        color: active ? DC.primary : DC.grey,
        font: "600 12px/1 var(--bsp-font-sans)",
        cursor: "pointer",
      }}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}

// 4-priority pill row: None / Low / Med / High. The "None" pill maps
// to a null priority on submit; everything else maps to the matching
// TODO_PRIORITY enum value. Each pill carries its own colour so the
// row reads at a glance.
const PRIORITY_LABEL: Record<(typeof TODO_PRIORITY)[number], string> = {
  low: "Low",
  medium: "Med",
  high: "High",
  critical: "Crit",
};
const PRIORITY_TONE: Record<(typeof TODO_PRIORITY)[number], string> = {
  low: DC.grey,
  medium: DC.primary,
  high: DC.orange,
  critical: DC.red,
};

export function PrioritySelect({
  value,
  onChange,
}: {
  value: (typeof TODO_PRIORITY)[number] | null;
  onChange: (v: (typeof TODO_PRIORITY)[number] | null) => void;
}) {
  const cellStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    height: 38,
    padding: "0 10px",
    border: `1px solid ${DC.borderStrong}`,
    background: DC.surface,
    color: DC.grey,
    font: "600 12px/1 var(--bsp-font-sans)",
    cursor: "pointer",
  };
  return (
    <div
      role="radiogroup"
      style={{ display: "flex", borderRadius: 6, overflow: "hidden" }}
    >
      <button
        type="button"
        role="radio"
        aria-checked={value === null}
        onClick={() => onChange(null)}
        style={{
          ...cellStyle,
          borderRight: 0,
          color: value === null ? DC.primary : DC.grey,
          background: value === null ? DC.primaryTint : DC.surface,
        }}
      >
        <span style={{ marginRight: 2 }}>·</span> None
      </button>
      {TODO_PRIORITY.map((p, i) => {
        const active = value === p;
        const last = i === TODO_PRIORITY.length - 1;
        return (
          <button
            key={p}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(p)}
            style={{
              ...cellStyle,
              borderRight: last ? `1px solid ${DC.borderStrong}` : 0,
              color: active ? PRIORITY_TONE[p] : DC.grey,
              background: active
                ? `color-mix(in srgb, ${PRIORITY_TONE[p]} 12%, ${DC.surface})`
                : DC.surface,
            }}
          >
            <span style={{ color: PRIORITY_TONE[p], fontSize: 14 }}>·</span>
            <span>{PRIORITY_LABEL[p]}</span>
          </button>
        );
      })}
    </div>
  );
}

// Tiny remove-pill button shared by labels / mentions.
export function PillRemoveBtn({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} style={{ ...iconBtn, width: 18, height: 18, color: DC.primary }}>
      <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>×</span>
    </button>
  );
}
